/**
 * AGENZ brand system.
 *
 * Every value here was extracted from the agency's own deck (docs/A gen Z.pptx)
 * — colour frequencies come from the raw slide XML, so `acid` really is the
 * dominant accent rather than a guess.
 *
 * This object is the *default* theme. It seeds SiteSettings.theme, after which
 * the admin theme editor owns it and renders it as CSS custom properties at
 * SSR time. Changing a colour in admin restyles the live site with no rebuild.
 */

export const COLORS = {
  /** Slide backgrounds, display type. */
  ink: '#000000',
  /** The signature AGENZ yellow — 48 uses across the deck, the loudest accent. */
  acid: '#F8F6AC',
  /** Sparkle/asterisk marks and links. */
  electric: '#102AAA',
  /** Secondary accent, used for emphasis and hover states. */
  blush: '#EF7299',
  /** Tertiary accent, pairs with acid for gradients and tags. */
  mint: '#D7F6AC',

  /**
   * Hero ground. Its own token rather than reusing an accent, so the agency
   * can re-pitch the hero without disturbing every other use of that colour.
   */
  heroSurface: '#EF7299',

  /** Off-white page background — pure white reads clinical against this palette. */
  paper: '#FAFAF7',
  /** Body copy on light surfaces. */
  slate: '#1A1A1A',
  muted: '#6B6B6B',
  line: '#E4E2D9',
};

/**
 * Accents rotate through this order wherever a sequence of cards or list items
 * needs to feel hand-composed rather than uniform.
 *
 * Use on dark surfaces, or for solid blocks (bars, fills) where the shape
 * carries the colour.
 */
export const ACCENT_CYCLE = [COLORS.acid, COLORS.electric, COLORS.blush, COLORS.mint];

/**
 * Accents for fine detail — icons, small marks, thin rules — on the pale
 * `paper` surface.
 *
 * Acid and mint are deliberately excluded: both are very light, and a 26px
 * sparkle in #F8F6AC on #FAFAF7 is effectively invisible. Solid blocks in those
 * colours are fine, small strokes are not.
 */
export const ACCENT_CYCLE_ON_LIGHT = [COLORS.electric, COLORS.blush, COLORS.ink];

export const FONTS = {
  /**
   * Display face. The deck uses `Brice Bold Condensed`, which is commercially
   * licensed and cannot ship in this repo — Anton is the closest free
   * condensed grotesque. The theme editor exposes this slot so AGENZ can drop
   * in licensed Brice files without a code change.
   */
  display: "'Anton', 'Brice Bold Condensed', 'Arial Narrow', sans-serif",
  /** Body face — Aileron is the deck's actual body font and is free/OFL. */
  body: "'Aileron', 'Inter', system-ui, -apple-system, sans-serif",
  /** Script accent, used sparingly for signature-style flourishes. */
  script: "'Caveat', 'Brilliant Signature 2', cursive",
  mono: "'JetBrains Mono', ui-monospace, 'SF Mono', monospace",
};

/** Fluid type scale — clamp() so headlines scale with viewport, not breakpoints. */
export const TYPE_SCALE = {
  xs: 'clamp(0.75rem, 0.72rem + 0.15vw, 0.8125rem)',
  sm: 'clamp(0.875rem, 0.84rem + 0.18vw, 0.9375rem)',
  base: 'clamp(1rem, 0.96rem + 0.2vw, 1.0625rem)',
  lg: 'clamp(1.125rem, 1.05rem + 0.35vw, 1.3125rem)',
  xl: 'clamp(1.375rem, 1.2rem + 0.8vw, 1.75rem)',
  '2xl': 'clamp(1.75rem, 1.4rem + 1.6vw, 2.5rem)',
  '3xl': 'clamp(2.25rem, 1.6rem + 3vw, 3.75rem)',
  '4xl': 'clamp(3rem, 1.8rem + 5.5vw, 5.5rem)',
  /** Hero-scale — "BUILT TO PERFORM." set edge to edge. */
  '5xl': 'clamp(3.5rem, 1.5rem + 9vw, 9rem)',
  '6xl': 'clamp(4rem, 0.5rem + 14vw, 14rem)',
};

export const RADII = {
  none: '0px',
  sm: '4px',
  md: '10px',
  lg: '20px',
  xl: '32px',
  pill: '999px',
};

/**
 * Neo-brutalist hard offsets — no blur. The deck's collage layering is built
 * from flat shadows, so these carry the aesthetic more than any other token.
 */
export const SHADOWS = {
  hard: '4px 4px 0 0 var(--agenz-shadow-color, #000)',
  hardLg: '8px 8px 0 0 var(--agenz-shadow-color, #000)',
  hardXl: '14px 14px 0 0 var(--agenz-shadow-color, #000)',
  lift: '0 12px 32px -8px rgba(0,0,0,0.18)',
};

export const SPACING = {
  section: 'clamp(4rem, 8vw, 9rem)',
  gutter: 'clamp(1.25rem, 4vw, 4rem)',
  maxWidth: '1440px',
  contentWidth: '72ch',
};

export const MOTION = {
  /** Custom easing — a slight overshoot that reads as confident, not bouncy. */
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
  easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
  duration: { fast: 0.2, base: 0.45, slow: 0.8, marquee: 28 },
};

export const BREAKPOINTS = { sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 };

/** Viewport buckets heatmap coordinates are normalised into. */
export const VIEWPORT_BUCKETS = [360, 768, 1024, 1440, 1920];

/** Seeded into SiteSettings.theme; the admin editor mutates this shape. */
export const DEFAULT_THEME = {
  colors: COLORS,
  fonts: FONTS,
  typeScale: TYPE_SCALE,
  radii: RADII,
  shadows: SHADOWS,
  spacing: SPACING,
  motion: MOTION,
};

/**
 * Merges a stored theme over the defaults, one level into each group.
 *
 * A shallow spread is wrong here: `{...DEFAULT_THEME, ...stored}` replaces the
 * entire `colors` object with whatever was saved, so any token added to the
 * defaults afterwards silently disappears for every site that has ever saved a
 * theme. Merging per group means new tokens keep working and saved overrides
 * still win.
 */
export function mergeTheme(stored = {}) {
  const merged = { ...DEFAULT_THEME };

  for (const group of Object.keys(DEFAULT_THEME)) {
    const base = DEFAULT_THEME[group];
    const override = stored[group];

    merged[group] =
      base && typeof base === 'object' && !Array.isArray(base)
        ? { ...base, ...(override ?? {}) }
        : (override ?? base);
  }

  // Carry through any group the defaults do not define.
  for (const [group, value] of Object.entries(stored)) {
    if (!(group in merged)) merged[group] = value;
  }

  return merged;
}

/**
 * Flattens the theme into CSS custom properties for injection into the SSR
 * <style> block. Nested objects become `--agenz-<group>-<key>`.
 */
export function themeToCssVars(theme = DEFAULT_THEME) {
  const lines = [];

  const walk = (obj, prefix) => {
    for (const [key, value] of Object.entries(obj ?? {})) {
      const name = `${prefix}-${kebab(key)}`;
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        walk(value, name);
      } else if (value !== undefined && value !== null) {
        lines.push(`  ${name}: ${value};`);
      }
    }
  };

  walk(theme.colors, '--agenz-color');
  walk(theme.fonts, '--agenz-font');
  walk(theme.typeScale, '--agenz-text');
  walk(theme.radii, '--agenz-radius');
  walk(theme.shadows, '--agenz-shadow');
  walk(theme.spacing, '--agenz-space');

  return `:root {\n${lines.join('\n')}\n}`;
}

function kebab(str) {
  return String(str)
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
}
